// apps/client/src/pages/dashboard/components/ProjectSummaryCard.tsx
import { Project, ProjectSummaryResponse } from '@avanzu/types';
import {
  CheckCircleOutline,
  ReceiptOutlined,
  ScienceOutlined,
  TrendingUpOutlined,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { apiClient } from '../../../services';
import formatCurrency from '../../../utils/formatCurrency';
import { getTransactionQuotaInfo } from '../../../utils/quota';

interface ProjectSummaryCardProps {
  project: Project;
  organisationId: string;
  isSelected: boolean;
  onSelect: (projectId: string) => void;
}

const ProjectSummaryCard: React.FC<ProjectSummaryCardProps> = ({
  project,
  organisationId,
  isSelected,
  onSelect,
}) => {
  const { currentOrganisation } = useAuth();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<ProjectSummaryResponse['summary'] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Get transaction quota information
  const quotaInfo = getTransactionQuotaInfo(currentOrganisation, project.transactionCount || 0);

  useEffect(() => {
    loadSummary();
  }, [project.id, organisationId]);

  const loadSummary = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get(
        `/organisations/${organisationId}/projects/${project.id}/summary`
      );
      const data: ProjectSummaryResponse = await response.json();

      if (data.success) {
        setSummary(data.summary);
      } else {
        setError('Assign your first transaction, employee or asset to see project summary');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load summary');
    } finally {
      setLoading(false);
    }
  };

  const handleClick = () => {
    onSelect(project.id);
  };

  return (
    <Box sx={{ position: 'relative', height: '100%' }}>
      {' '}
      {/* Add height: '100%' here */}
      {isSelected && (
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            top: -12,
            zIndex: 1,
          }}
        >
          <Chip
            icon={<CheckCircleOutline sx={{ fontSize: 16 }} />}
            label="Selected"
            size="small"
            color="success"
          />
        </Box>
      )}
      <Card
        sx={{
          height: '100%',
          border: isSelected ? 2 : 1,
          padding: isSelected ? '0px' : '1px',
          borderColor: isSelected ? 'success.main' : 'divider',
          position: 'relative',

          '&:hover': {
            borderColor: 'primary.main',
            boxShadow: 3,
          },
        }}
      >
        <Box
          onClick={handleClick}
          sx={{
            height: '100%',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <CardContent
            sx={{
              p: 3,
              height: '100%',
              flex: 1,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Header */}
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Chip
                  label={`FY ${project.taxYear}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Stack>
              <Typography
                variant="h6"
                fontWeight={600}
                sx={{
                  mb: 0.5,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  height: '3em', // Reserve space for 2 lines
                  lineHeight: 1.5,
                }}
              >
                {project.name.substring(0, 80)}
                {project.name.length > 80 ? '...' : ''}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: 0,
                  height: '4em', // Reserve space for 2 lines
                }}
              >
                {project.description && project.description.substring(0, 90)}
                {project.description && project.description?.length > 90 ? '...' : ''}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                fullWidth
                sx={{ mt: 1 }}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect(project.id);
                  navigate(`/app/summary/${project.id}`);
                }}
              >
                View project report
              </Button>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Summary Content */}
            {loading ? (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 4,
                }}
              >
                <CircularProgress size={32} />
              </Box>
            ) : error ? (
              <Box sx={{ flex: 1, py: 2 }}>
                <Typography variant="body2" color="error" align="center">
                  {error}
                </Typography>
              </Box>
            ) : summary ? (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Actual Totals */}
                <Box sx={{ mb: 2 }}>
                  <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                        Total Cost
                      </Typography>
                      <Typography variant="h5" fontWeight={700} color="primary.main">
                        {formatCurrency(summary.grandTotal)}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                        Tax Offset
                      </Typography>
                      <Typography variant="h5" fontWeight={700} color="success.main">
                        {formatCurrency(summary.totalTaxOffset)}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Cost Breakdown */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Expenses
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {formatCurrency(summary.totalExpenses)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Wages
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {formatCurrency(summary.totalWages)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Super
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {formatCurrency(summary.totalSuper)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Assets
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {formatCurrency(summary.totalAssets)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Divider sx={{ my: 2 }} />

                {/* Projected Totals (if current year) */}
                {summary.isCurrentYear && summary.projectedGrandTotal !== undefined && (
                  <>
                    <Box
                      sx={{
                        p: 1.5,
                      }}
                    >
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
                        <TrendingUpOutlined sx={{ fontSize: 16, color: 'info.main' }} />
                        <Typography variant="caption" color="info.main" fontWeight={600}>
                          Projected
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                          ({summary.daysElapsed} of {summary.totalDays} days)
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={2}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Total Cost
                          </Typography>
                          <Typography variant="body1" fontWeight={600} color="info.main">
                            {formatCurrency(summary.projectedGrandTotal)}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Tax Offset
                          </Typography>
                          <Typography variant="body1" fontWeight={600} color="success.main">
                            {formatCurrency(summary.projectedTotalTaxOffset || 0)}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  </>
                )}

                {/* Spacer to push content to bottom */}
                <Box sx={{ flexGrow: 1 }} />

                {/* Transaction Quota Progress - always at bottom */}
                <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  {quotaInfo.isAtLimit ? (
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'error.main',
                          fontWeight: 600,
                          display: 'block',
                          textAlign: 'center',
                          mb: 1,
                        }}
                      >
                        Transaction limit reached
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        fullWidth
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/settings/plan');
                        }}
                      >
                        Upgrade account
                      </Button>
                    </Box>
                  ) : quotaInfo.isUnlimited ? (
                    // Show activities and transactions count when unlimited
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <ScienceOutlined sx={{ fontSize: 20, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {summary.activities.length}{' '}
                          {summary.activities.length === 1 ? 'activity' : 'activities'}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <ReceiptOutlined sx={{ fontSize: 20, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {quotaInfo.current}{' '}
                          {quotaInfo.current === 1 ? 'transaction' : 'transactions'}
                        </Typography>
                      </Stack>
                    </Stack>
                  ) : (
                    // Show progress bar when not at limit and not unlimited
                    <Box>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mb: 0.5 }}
                      >
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <ReceiptOutlined sx={{ fontSize: 20, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            Transactions
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {quotaInfo.current} / {quotaInfo.limit}
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={(quotaInfo.current / quotaInfo.limit) * 100}
                        sx={{
                          height: 6,
                          borderRadius: 1,
                          backgroundColor: 'grey.200',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: quotaInfo.isNearLimit
                              ? 'warning.main'
                              : 'primary.main',
                          },
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            ) : (
              <Box sx={{ flex: 1, py: 2 }}>
                <Typography variant="body2" color="text.secondary" align="center">
                  No data available
                </Typography>
              </Box>
            )}
          </CardContent>
        </Box>
      </Card>
    </Box>
  );
};

export default ProjectSummaryCard;
